import { useState } from 'react';
import { useSelector } from '@legendapp/state/react';
import { Sparkles, Github, Lock, Mail, User, AlertCircle, X } from 'lucide-react';
import Modal from './Modal';
import { Capacitor } from '@capacitor/core';
import { signInWithGoogle, signInWithGitHub, signInWithApple } from '@/lib/auth';
import { nativeAppleLogin } from '@/lib/nativeAuth';
import { signInAnonymously, login, register, upgradeAnonymousAccount } from '@/stores/auth$';
import { auth$ } from '@/stores/auth$';
import { supabase } from '@/lib/supabaseClient';

function AppleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.39.07 2.35.74 3.14.76 1.19-.24 2.33-.93 3.6-.84 1.54.12 2.7.72 3.47 1.82-3.16 1.93-2.42 5.85.53 7.02-.62 1.68-1.44 3.34-2.74 4.12zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}

function GoogleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

/**
 * AuthModal - Unified single-screen authentication
 * OAuth + email form + sign in/sign up tabs all visible at once.
 * No hidden screens or extra clicks.
 */
export default function AuthModal({ mode = 'auth', isOpen, onClose }) {
  const isAuthenticated = useSelector(() => auth$.isAuthenticated.get());
  const isLoading = useSelector(() => auth$.isLoading.get());

  // Default to 'signup' — new app, most visitors are first-time users
  const [formMode, setFormMode] = useState('signup'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  if (mode === 'auth' && isLoading) return null;
  if (mode === 'auth' && isAuthenticated) return null;
  if (mode === 'upgrade' && !isOpen) return null;

  const clearMessages = () => { setError(''); setMessage(''); };

  const handleOAuthLogin = async (provider) => {
    clearMessages();
    setLoading(true);
    try {
      if (provider === 'google') await signInWithGoogle();
      else if (provider === 'github') await signInWithGitHub();
      else if (provider === 'apple') {
        if (Capacitor.getPlatform() === 'ios') await nativeAppleLogin();
        else await signInWithApple();
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    clearMessages();
    setLoading(true);
    try {
      await signInAnonymously();
    } catch (err) {
      setError(err.message || 'Failed to sign in as guest');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);

    try {
      if (mode === 'upgrade') {
        await upgradeAnonymousAccount(email, username || email.split('@')[0], password);
        setMessage('Account upgraded! Redirecting...');
        setTimeout(() => { if (onClose) onClose(); }, 1500);
      } else if (formMode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) throw error;
        setMessage('Password reset email sent! Check your inbox.');
        setFormMode('signin');
      } else if (formMode === 'signup') {
        await register(email, username || email.split('@')[0], password);
        setMessage('Verification email sent! Check your inbox, then sign in.');
        setFormMode('signin');
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    clearMessages();
    setFormMode(newMode);
  };

  const isSignIn = formMode === 'signin';
  const isSignUp = formMode === 'signup';
  const isReset = formMode === 'reset';
  const isUpgrade = mode === 'upgrade';

  return (
    <Modal isOpen onClose={onClose} overlayClassName="px-0 md:px-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-lg p-8 animate-in fade-in zoom-in duration-300 relative">
        {/* Close button — upgrade mode only */}
        {isUpgrade && onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-secondary"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        )}

        {/* Header */}
        <div className="flex flex-col items-center text-center space-y-3 mb-6">
          <div className="hidden md:flex h-12 w-12 bg-primary/10 rounded-full items-center justify-center text-primary">
            <Sparkles size={24} />
          </div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">
            {isUpgrade ? 'Upgrade Your Account' : 'Welcome to PromptNote'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {isUpgrade
              ? 'Save your notes permanently and sync across devices.'
              : isReset
                ? 'Enter your email to receive a reset link.'
                : 'A versioned notepad for your creative prompts.'
            }
          </p>
        </div>

        {/* OAuth — hide during password reset */}
        {!isReset && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <button
                onClick={() => handleOAuthLogin('google')}
                disabled={loading}
                className="h-10 flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-md transition-colors font-medium text-sm disabled:opacity-50"
              >
                <GoogleIcon size={16} /> Google
              </button>
              <button
                onClick={() => handleOAuthLogin('github')}
                disabled={loading}
                className="h-10 flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-md transition-colors font-medium text-sm disabled:opacity-50"
              >
                <Github size={16} /> GitHub
              </button>
              <button
                onClick={() => handleOAuthLogin('apple')}
                disabled={loading}
                className="h-10 flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-md transition-colors font-medium text-sm disabled:opacity-50"
              >
                <AppleIcon size={16} /> Apple
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or continue with email</span>
              </div>
            </div>
          </>
        )}

        {/* Sign In / Sign Up tabs — not shown for upgrade or reset */}
        {!isUpgrade && !isReset && (
          <div className="flex mb-4 bg-secondary rounded-lg p-1">
            <button
              type="button"
              onClick={() => switchMode('signup')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                isSignUp
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign Up
            </button>
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                isSignIn
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
          </div>
        )}

        {/* Email form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full pl-10 pr-4 py-2.5 bg-secondary border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
          </div>

          {/* Username — signup and upgrade only */}
          {(isSignUp || isUpgrade) && (
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="text"
                placeholder="Username (optional)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="w-full pl-10 pr-4 py-2.5 bg-secondary border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
          )}

          {/* Password — hide for reset */}
          {!isReset && (
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                className="w-full pl-10 pr-4 py-2.5 bg-secondary border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
          )}

          {/* Forgot password link — sign in mode only */}
          {isSignIn && !isUpgrade && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => switchMode('reset')}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="flex-shrink-0 mt-0.5 text-destructive" size={16} />
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}

          {/* Success */}
          {message && (
            <div className="flex items-start gap-2 p-3 bg-primary/10 border border-primary/20 rounded-md">
              <AlertCircle className="flex-shrink-0 mt-0.5 text-primary" size={16} />
              <p className="text-xs text-primary">{message}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? 'Loading...'
              : isUpgrade
                ? 'Upgrade Account'
                : isReset
                  ? 'Send Reset Email'
                  : isSignIn
                    ? 'Sign In'
                    : 'Create Account'
            }
          </button>
        </form>

        {/* Privacy & Terms consent — shown below the main CTA for all non-reset flows */}
        {!isReset && (
          <p className="mt-3 text-center text-[11px] text-muted-foreground leading-relaxed">
            By continuing, you agree to our{' '}
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground transition-colors">Privacy Policy</a>
            {' '}and{' '}
            <a href="/termofservice.html" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground transition-colors">Terms of Service</a>.
          </p>
        )}

        {/* Reset mode — back link */}
        {isReset && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Back to <span className="text-primary font-medium">Sign In</span>
            </button>
          </div>
        )}

        {/* Guest option — auth mode, not reset */}
        {!isUpgrade && !isReset && (
          <>
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGuestLogin}
              disabled={loading}
              className="w-full h-10 flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-md transition-colors font-medium text-sm disabled:opacity-50"
            >
              Continue as Guest
            </button>

            <p className="mt-3 text-xs text-center text-muted-foreground">
              Start right away. You can create an account anytime to sync across devices.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
